import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  PhoneAndroid,
  Email,
  UploadFile,
  CheckCircle,
} from '@mui/icons-material';
import axios from 'axios';
import firebase from 'firebase/app';
import 'firebase/storage';
import { ENDPOINTS } from '../../config';

const ClaimVerify = () => {
  const { venueId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { sessionId, verificationType, venue } = location.state || {};

  const [verificationCode, setVerificationCode] = useState('');
  const [email, setEmail] = useState('');
  const [documentFile, setDocumentFile] = useState(null);
  const [documentType, setDocumentType] = useState('abn');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);
  const [emailSent, setEmailSent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const errorRef = useRef(null);
  const codeInputRef = useRef(null);

  useEffect(() => {
    document.title = 'Verify Your Identity — Step 2 of 4 | Piatto';
  }, []);

  useEffect(() => {
    if (!sessionId || !verificationType) {
      setError('Invalid verification session. Please restart the claim process.');
    }
  }, [sessionId, verificationType]);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  // Move focus to code input once email has been sent
  useEffect(() => {
    if (emailSent && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [emailSent]);

  const handlePhoneVerification = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await axios.post(
        ENDPOINTS.verifyPhone,
        { sessionId, verificationCode }
      );

      setSuccess(true);
      setTimeout(() => {
        history.push(`/claim-venue/${venueId}/create-account`, { sessionId, venue });
      }, 1500);
    } catch (err) {
      console.error('Error verifying phone:', err);
      const remaining = err.response?.data?.attemptsRemaining || 0;
      setAttemptsRemaining(remaining);
      setError(
        err.response?.data?.error ||
        'Invalid verification code. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmailCode = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await axios.post(
        ENDPOINTS.verifyEmailSend,
        { sessionId, email }
      );

      setEmailSent(true);
      setError(null);
    } catch (err) {
      console.error('Error sending email:', err);
      setError(
        err.response?.data?.error ||
        'Failed to send verification email. Please check your email address.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEmailVerification = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await axios.post(
        ENDPOINTS.verifyEmailConfirm,
        { sessionId, verificationCode }
      );

      setSuccess(true);
      setTimeout(() => {
        history.push(`/claim-venue/${venueId}/create-account`, { sessionId, venue });
      }, 1500);
    } catch (err) {
      console.error('Error verifying email:', err);
      const remaining = err.response?.data?.attemptsRemaining || 0;
      setAttemptsRemaining(remaining);
      setError(
        err.response?.data?.error ||
        'Invalid verification code. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async () => {
    if (!documentFile) {
      setError('Please select a document to upload');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const documentUrl = await uploadDocument(documentFile);

      await axios.post(
        ENDPOINTS.uploadVerificationDocument,
        { sessionId, documentUrl, documentType }
      );

      setSuccess(true);
      setError(null);
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(
        err.response?.data?.error ||
        'Failed to upload document. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  const uploadDocument = (file) => new Promise((resolve, reject) => {
    const ext = file.name.split('.').pop();
    const path = `claim-documents/${sessionId}/${Date.now()}.${ext}`;
    const uploadTask = firebase.storage().ref(path).put(file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setUploadProgress(pct);
      },
      (err) => reject(err),
      async () => {
        const downloadUrl = await uploadTask.snapshot.ref.getDownloadURL();
        resolve(downloadUrl);
      },
    );
  });

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} aria-hidden="true" />
        <Typography variant="h4" gutterBottom>
          Verified Successfully!
        </Typography>
        <Typography color="text.secondary" aria-live="polite">
          Redirecting to account creation...
        </Typography>
        <LinearProgress sx={{ mt: 3 }} aria-label="Redirecting, please wait" />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Verify Your Identity
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {venue?.bizName || 'Your Venue'}
        </Typography>
      </Box>

      <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              role="alert"
              ref={errorRef}
              tabIndex={-1}
            >
              {error}
              {attemptsRemaining > 0 && attemptsRemaining < 5 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Attempts remaining: {attemptsRemaining}
                </Typography>
              )}
            </Alert>
          )}

          {/* Phone Verification */}
          {verificationType === 'phone' && (
            <Stack spacing={3}>
              <Box>
                <PhoneAndroid sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} aria-hidden="true" />
                <Typography variant="h6" component="h2" gutterBottom>
                  Phone Verification
                </Typography>
                <Typography color="text.secondary" paragraph>
                  We've sent a 6-digit code to your venue's phone number.
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                required
                inputProps={{
                  maxLength: 6,
                  style: { fontSize: '1.5rem', textAlign: 'center' },
                  'aria-required': 'true',
                  'aria-describedby': 'phone-code-hint',
                }}
                disabled={loading}
              />
              <Typography id="phone-code-hint" variant="body2" color="text.secondary" sx={{ mt: -2 }}>
                Enter the 6-digit code sent to your phone
              </Typography>

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handlePhoneVerification}
                disabled={loading || verificationCode.length !== 6}
                aria-busy={loading}
                sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 600 }}
              >
                {loading ? <CircularProgress size={24} aria-hidden="true" /> : 'Verify Phone'}
              </Button>
            </Stack>
          )}

          {/* Email Verification */}
          {verificationType === 'email' && (
            <Stack spacing={3}>
              <Box>
                <Email sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} aria-hidden="true" />
                <Typography variant="h6" component="h2" gutterBottom>
                  Email Verification
                </Typography>
                <Typography color="text.secondary" paragraph>
                  Enter your business email address to receive a verification code.
                </Typography>
              </Box>

              {!emailSent ? (
                <>
                  <TextField
                    fullWidth
                    label="Business Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="manager@yourvenue.com"
                    required
                    disabled={loading}
                    inputProps={{ 'aria-required': 'true' }}
                    helperText="Must be from your website's domain"
                  />

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleSendEmailCode}
                    disabled={loading || !email}
                    aria-busy={loading}
                    sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 600 }}
                  >
                    {loading ? <CircularProgress size={24} aria-hidden="true" /> : 'Send Verification Code'}
                  </Button>
                </>
              ) : (
                <>
                  <Alert severity="success" role="status">
                    Verification code sent to {email}
                  </Alert>

                  <TextField
                    fullWidth
                    label="Verification Code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    required
                    inputProps={{
                      maxLength: 6,
                      style: { fontSize: '1.5rem', textAlign: 'center' },
                      'aria-required': 'true',
                      'aria-describedby': 'email-code-hint',
                      ref: codeInputRef,
                    }}
                    helperText="Enter the 6-digit code sent to your email"
                    FormHelperTextProps={{ id: 'email-code-hint' }}
                    disabled={loading}
                  />

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleEmailVerification}
                    disabled={loading || verificationCode.length !== 6}
                    aria-busy={loading}
                    sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 600 }}
                  >
                    {loading ? <CircularProgress size={24} aria-hidden="true" /> : 'Verify Email'}
                  </Button>

                  <Button
                    variant="text"
                    size="small"
                    onClick={() => { setEmailSent(false); setVerificationCode(''); }}
                  >
                    Use different email
                  </Button>
                </>
              )}
            </Stack>
          )}

          {/* Document Upload */}
          {verificationType === 'document' && (
            <Stack spacing={3}>
              <Box>
                <UploadFile sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} aria-hidden="true" />
                <Typography variant="h6" component="h2" gutterBottom>
                  Document Upload
                </Typography>
                <Typography color="text.secondary" paragraph>
                  Upload business documentation to verify ownership.
                </Typography>
              </Box>

              <TextField
                select
                fullWidth
                label="Document Type"
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                SelectProps={{ native: true }}
                inputProps={{ 'aria-label': 'Select document type' }}
              >
                <option value="abn">ABN Registration</option>
                <option value="utility_bill">Utility Bill</option>
                <option value="business_license">Business License</option>
              </TextField>

              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ py: 2 }}
                aria-label={documentFile ? `Change file. Currently selected: ${documentFile.name}` : 'Choose file to upload'}
              >
                {documentFile ? documentFile.name : 'Choose File'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  aria-label="Upload verification document (PDF, JPG, or PNG)"
                  onChange={(e) => setDocumentFile(e.target.files[0])}
                />
              </Button>

              {documentFile && (
                <Alert severity="info" role="status">
                  Selected: {documentFile.name}
                </Alert>
              )}

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleDocumentUpload}
                disabled={uploading || !documentFile}
                aria-busy={uploading}
                sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 600 }}
              >
                {uploading ? `Uploading… ${uploadProgress}%` : 'Upload Document'}
              </Button>

              {uploading && (
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  aria-label={`Upload progress: ${uploadProgress}%`}
                />
              )}

              {success && (
                <Alert severity="success" role="status">
                  Document uploaded successfully! Our team will review it within 24–48 hours.
                  You'll receive an email once approved.
                </Alert>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ClaimVerify;
